import { MeasurementController } from ".";
import {Circle as CircleStyle, Fill, Stroke, Style} from "ol/style";
import {Vector as VectorSource} from "ol/source";
import {Vector as VectorLayer} from "ol/layer";
import {Draw} from "ol/interaction";
import {getArea, getLength} from 'ol/sphere';
import Overlay from 'ol/Overlay';
import {unByKey} from 'ol/Observable';
import Feature from 'ol/Feature';
import Polygon from 'ol/geom/Polygon';
import './aerial-tooltip.css';
import { setAerialTooltip } from "../../viewer";

export class MinimapMeasure {
  private controller: MeasurementController;
  private sourceStandard: VectorSource;
  private vectorStandard: VectorLayer;
  private sourceHover: VectorSource
  private vectorHover: VectorLayer

  private pointerMoveHandler;
  public enabled = false;
  public measurementType = null;
  public sketch = null;
  private measureTooltipElement = null;
  private measureTooltip = null;
  private draw = null;
  private initialized = false;

  constructor(controller) {
    this.controller = controller;
  }

  get map() {
    return this.controller.viewer.minimap.map;
  }

  get mapElement() {
    return this.controller.viewer.minimap.containerElement;
  }

  get moreTooltip() {
    let coordinates = this.sketch.getGeometry().flatCoordinates;
    let numPoints = (coordinates.length / 2.0) - 1;

    let canFinish = (this.measurementType === "Polygon")
      ? numPoints > 2
      : true;

    const tooltipText = [
      `Click to add more points (${numPoints})`,
      ...canFinish ? ["Double-click to finish"] : [],
      "Right-click to cancel"
    ];

    return tooltipText;
  }

  get startTooltip() {
    if (this.measurementType === "LineString") {
      return ["Click to start drawing line"];
    }

    if (this.measurementType === "Polygon") {
      return ["Click to start drawing polygon"];
    }

    return ["Invalid measure type"];
  }

  setMeasurementType(measurementType) {
    // Only standard and area and valid for aerial
    if (measurementType === 'standard') {
      this.setAsLength();
    } else if (measurementType === 'area') {
      this.setAsArea();
    } else {
      this.setAsNull();
    }
  }

  setAsLength() {
    this.measurementType = "LineString";
    this.reloadInteraction();
  }

  setAsArea() {
    this.measurementType = "Polygon";
    this.reloadInteraction();
  }

  setAsNull() {
    this.measurementType = null;
    this.reloadInteraction();
    this.removeInteraction();
  }

  init() {
    if (this.initialized) {
      return;
    }

    let fill = new Fill({
      color: 'rgba(255, 255, 255, 0.2)'
    });

    let image = new CircleStyle({
      radius: 7,
      fill: new Fill({
        color: '#008B8B'
      })
    });

    this.sourceStandard = new VectorSource({
      wrapX: false,
      noWrap: true
    });

    this.vectorStandard = new VectorLayer({
      type: 'measure',
      source: this.sourceStandard,
      style: new Style({
        fill: fill,
        image: image,
        stroke: new Stroke({
          color: '#008B8B',
          width: 2
        })
      })
    });

    this.sourceHover = new VectorSource({
      wrapX: false,
      noWrap: true
    });

    this.vectorHover = new VectorLayer({
      type: 'measure',
      source: this.sourceHover,
      style: new Style({
        fill: fill,
        image: image,
        stroke: new Stroke({
          color: '#ffcc33',
          width: 2
        })
      })
    });

    this.vectorStandard.setZIndex(1);
    this.vectorHover.setZIndex(1);

    this.map.addLayer(this.vectorStandard);
    this.map.addLayer(this.vectorHover);

    this.addInteraction();
    this.initEvents();
    this.initialized = true;
  }

  initEvents() {
    this.mapElement.addEventListener('mouseenter', () => {
      if (this.controller.enabled) {
        this.addInteraction();
      }
    });

    this.mapElement.addEventListener('mouseleave', () => {
      if (this.controller.enabled) {
        this.removeInteraction();
      }
    });
  }

  onMouseMove(event) {
    if (event.dragging || !this.controller.enabled) {
      return;
    }

    let helpMsg = this.sketch
      ? this.moreTooltip
      : this.startTooltip;

    setAerialTooltip(helpMsg);
  }

  setMeasureTooltipInfo(message, coordinates, offset = null) {
    this.measureTooltipElement.innerHTML = message;
    this.measureTooltip.setPosition(coordinates);
    if (offset) {
      this.measureTooltip.setOffset(offset);
    }
  }

  reloadInteraction() {
    if (this.map) {
      if (this.draw) {
        this.map.removeInteraction(this.draw);
      }

      this.sketch = null;
      this.addInteraction();
    }
  }

  clear() {
    if (!this.initialized) {
      return;
    }

    let elements = document.getElementsByClassName("tooltip-minimap");
    Array.from(elements).forEach(element => {
      element.remove();
    });

    if (this.sourceStandard) {
      this.sourceStandard.clear();
    }

    if (this.sourceHover) {
      this.sourceHover.clear();
    }

    this.removeInteraction();

    this.sketch = null;

    this.createMeasureTooltip();
    this.addInteraction();
  }

  removeInteraction() {
    if (this.draw) {
      this.map.removeInteraction(this.draw);
    }
  }

  addInteraction() {
    if (this.measurementType === null || this.map === null) {
      return;
    }

    if (!this.controller.editable) {
      return;
    }

    this.removeInteraction();

    this.draw = new Draw({
      source: this.sourceStandard,
      type: this.measurementType,
      condition: event => {
        if (event.originalEvent.button === 0) {
          return true;
        }

        if (event.originalEvent.button === 2) {
          if (this.controller.enabled) {
            this.reloadInteraction();
            setAerialTooltip(this.startTooltip);
          }
        }

        return false;
      },
      style: new Style({
        fill: new Fill({
          color: 'rgba(255, 255, 255, 0.2)'
        }),
        stroke: new Stroke({
          color: 'rgba(0, 0, 0, 0.5)',
          lineDash: [10, 10],
          width: 2
        }),
        image: new CircleStyle({
          radius: 5,
          stroke: new Stroke({
            color: 'rgba(0, 0, 0, 0.7)'
          }),
          fill: new Fill({
            color: 'rgba(255, 255, 255, 0.2)'
          })
        })
      })
    });
    this.map.addInteraction(this.draw);
    this.createMeasureTooltip();

    let listener;

    this.draw.on('drawstart', event => {
      this.sketch = event.feature;

      listener = this.sketch.getGeometry().on('change', event => {
        let geometry = event.target;
        let [output, tooltipCoord] = this.getTooltipValues(geometry);
        this.setMeasureTooltipInfo(output, tooltipCoord);
      });
    }, this);

    this.draw.on('drawend', (event) => {
      this.measureTooltipElement.className = 'tooltip tooltip-minimap tooltip-static';
      this.measureTooltip.setOffset([0, -7]);
      this.sketch = null;
      this.measureTooltipElement = null;
      this.createMeasureTooltip();
      unByKey(listener);

      this.controller.addAerialMeasurement(event.feature.getGeometry());
      setAerialTooltip(this.startTooltip);
    }, this);
  }

  createMeasureTooltip() {
    if (this.measureTooltipElement) {
      let measureParent = this.measureTooltipElement.parentNode;
      if (measureParent) {
        measureParent.removeChild(this.measureTooltipElement);
      }
    }

    this.measureTooltipElement = document.createElement('div');
    this.measureTooltipElement.className = 'tooltip tooltip-minimap tooltip-measure';
    this.measureTooltip = new Overlay({
      element: this.measureTooltipElement,
      offset: [0, -15],
      positioning: 'bottom-center'
    });
    this.map.addOverlay(this.measureTooltip);
  }

  loadGeometry(measurement, hover = false) {
    let geometry = measurement.geometry;
    let feature = new Feature(geometry);
    feature.measurementID = measurement.id;

    if (hover) {
      this.sourceHover.addFeature(feature);
    } else {
      this.sourceStandard.addFeature(feature);
    }

    let [output, tooltipCoord] = this.getTooltipValues(geometry);

    this.createMeasureTooltip();
    this.measureTooltipElement.className = 'tooltip tooltip-minimap tooltip-static';
    this.setMeasureTooltipInfo(output, tooltipCoord, [0, -7]);
    this.measureTooltipElement = null;

    this.createMeasureTooltip();
  }

  getTooltipValues(geometry) {
    let output;
    let tooltipCoord;

    let formatter = this.controller.formatter;

    if (geometry instanceof Polygon) {
      let area = getArea(geometry);
      output = formatter.formatArea(area);
      tooltipCoord = geometry.getInteriorPoint().getCoordinates();
    } else {
      let length = getLength(geometry);
      output = formatter.formatLength(length);
      tooltipCoord = geometry.getLastCoordinate();
    }
    return [output, tooltipCoord];
  }
}
